import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Users,
  FileText,
  Sparkles,
  Loader2,
  AlertTriangle,
  Globe,
  Wand2,
} from "lucide-react";
import { API } from "../Sidebar";
import { Link } from "react-router-dom";

const REP_API = `${API}/reputation`;

const STAT_CARDS = [
  { key: "entities",  label: "Entities",         icon: Sparkles,  color: "from-blue-500 to-blue-600" },
  { key: "pages",     label: "Pages Generated",  icon: FileText,  color: "from-slate-500 to-slate-600" },
  { key: "leads",     label: "Total Leads",      icon: Users,     color: "from-orange-500 to-orange-600" },
  { key: "new_leads", label: "New Leads",        icon: AlertTriangle, color: "from-rose-500 to-rose-600" },
  { key: "analyses",  label: "Case Analyses",    icon: ShieldCheck, color: "from-emerald-500 to-emerald-600" },
  { key: "ai_blocks", label: "AI Blocks Cached", icon: Wand2,     color: "from-purple-500 to-purple-600" },
];

export const ReputationDashboard = () => {
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${REP_API}/dashboard/summary`);
      setSummary(data || {});
    } catch (e) {
      toast.error("Failed to load Reputation Recovery dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async (force = false) => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${REP_API}/seed${force ? "?force=true" : ""}`);
      toast.success(`Seed complete · +${data.counts.entities_inserted} new · ${data.counts.entities_updated} updated`);
      load();
    } catch (e) {
      toast.error("Seed failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reputation-loading">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reputation-dashboard">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>
            Reputation Recovery
          </h1>
          <p className="text-slate-500 mt-1">
            FakeReviewRemovalPro.com — Google review removal lead-gen platform.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSeed(false)}
            disabled={seeding}
            data-testid="seed-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Initialize Seed
          </Button>
          <Button
            onClick={() => handleSeed(true)}
            disabled={seeding}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="seed-force-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Reseed (force)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stat-cards">
        {STAT_CARDS.map((s) => {
          const Icon = s.icon;
          const value = summary[s.key] ?? 0;
          return (
            <Card key={s.key} className="p-5 flex items-center gap-4" data-testid={`stat-${s.key}`}>
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${s.color} text-white flex items-center justify-center`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <div className="text-2xl font-bold tabular-nums">{value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-6" data-testid="next-steps-card">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Globe className="w-5 h-5 text-orange-500" />
          Phase 1 — Quick links
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link to="/admin/reputation/leads" className="block p-4 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors" data-testid="link-leads">
            <div className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Leads</div>
            <div className="text-sm text-slate-500 mt-1">View Free Audit form submissions</div>
          </Link>
          <Link to="/admin/reputation/ai" className="block p-4 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors" data-testid="link-ai">
            <div className="font-semibold flex items-center gap-2"><Wand2 className="w-4 h-4" /> AI Content Generator</div>
            <div className="text-sm text-slate-500 mt-1">Generate GPT-5.2 SEO blocks (Étape 3)</div>
          </Link>
          <Link to="/admin/reputation/publish" className="block p-4 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors" data-testid="link-publish">
            <div className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> WordPress Publish</div>
            <div className="text-sm text-slate-500 mt-1">Publish generated pages to WP (Étape 5)</div>
          </Link>
          <a href="/app/memory/REPUTATION_RECOVERY_STRATEGY.md" className="block p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors" data-testid="link-strategy">
            <div className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Strategy Doc</div>
            <div className="text-sm text-slate-500 mt-1">Full strategy + decisions in /app/memory/</div>
          </a>
        </div>
      </Card>
    </div>
  );
};

export default ReputationDashboard;
